// Personnel register with dietary requirements editor.
// Sensitive details (medical, emergency, dietary) are only shown inside the edit form.
import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { RegisterPage, FieldSpec, useLookup, useNameLookup, boolCol } from '../crud';
import { fullName, ROSTER_PATTERNS, EMPLOYMENT_TYPES } from '../util';

const DIET_OPTIONS = ['No dietary requirements', 'Vegetarian', 'Vegan', 'Gluten free', 'Dairy free', 'Halal',
  'Nut allergy', 'Seafood allergy', 'Other allergy', 'Other requirement'];

export default function People() {
  const companies = useLookup('companies');
  const roles = useNameLookup('roles');
  const trades = useNameLookup('trades');

  const specs: FieldSpec[] = [
    { name: 'first_name', label: 'First name', type: 'text', required: true, section: 'Identity' },
    { name: 'last_name', label: 'Last name', type: 'text', required: true, section: 'Identity' },
    { name: 'preferred_name', label: 'Preferred name', type: 'text', section: 'Identity' },
    { name: 'company_id', label: 'Employer / company', type: 'select', lookup: 'companies', section: 'Identity' },
    { name: 'employment_type', label: 'Employment type', type: 'select', options: EMPLOYMENT_TYPES, default: 'Employee', section: 'Identity' },
    { name: 'trade', label: 'Trade', type: 'select', nameLookup: 'trades', section: 'Role' },
    { name: 'role', label: 'Role', type: 'select', nameLookup: 'roles', section: 'Role' },
    { name: 'project_position', label: 'Project position', type: 'text', section: 'Role' },
    { name: 'supervisor', label: 'Primary supervisor', type: 'text', section: 'Role' },
    { name: 'default_roster', label: 'Default roster pattern', type: 'select', options: ROSTER_PATTERNS, default: 'Custom', section: 'Role' },
    { name: 'phone', label: 'Phone', type: 'text', section: 'Contact' },
    { name: 'email', label: 'Email', type: 'email', section: 'Contact' },
    { name: 'home_location', label: 'Home location', type: 'text', section: 'Contact' },
    { name: 'emergency_name', label: 'Emergency contact name', type: 'text', section: 'Emergency & medical (sensitive)' },
    { name: 'emergency_phone', label: 'Emergency contact phone', type: 'text', section: 'Emergency & medical (sensitive)' },
    { name: 'medical_notes', label: 'Medical / accessibility notes', type: 'textarea', section: 'Emergency & medical (sensitive)' },
    { name: 'preferred_room_type', label: 'Preferred room type', type: 'text', section: 'Logistics' },
    { name: 'vehicle_required', label: 'Vehicle required', type: 'bool', section: 'Logistics' },
    { name: 'flight_required', label: 'Flight required', type: 'bool', section: 'Logistics' },
    { name: 'active', label: 'Active', type: 'bool', default: 1, section: 'Logistics' },
    { name: 'notes', label: 'General notes', type: 'textarea', section: 'Logistics' }
  ];

  return (
    <RegisterPage
      title="Personnel"
      entity="people"
      specs={specs}
      lookupEntities={{ companies, roles, trades }}
      columns={[
        { key: 'name', label: 'Name', render: r => <strong>{fullName(r)}</strong>, sortValue: r => fullName(r) },
        { key: 'company_id', label: 'Company', render: r => companies.byId[r.company_id] || '', sortValue: r => companies.byId[r.company_id] || '' },
        { key: 'employment_type', label: 'Type' },
        { key: 'trade', label: 'Trade' },
        { key: 'role', label: 'Role' },
        { key: 'default_roster', label: 'Roster' },
        boolCol('active', 'Active')
      ]}
      onRowExtra={person => <DietaryEditor personId={person.id} />}
    />
  );
}

function DietaryEditor({ personId }: { personId: number }) {
  const [rows, setRows] = useState<any[]>([]);
  const [requirement, setRequirement] = useState('');
  const [isAllergy, setIsAllergy] = useState(false);
  const [notes, setNotes] = useState('');

  const load = () => api(`/dietary?person_id=${personId}`).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [personId]);

  const add = async () => {
    if (!requirement) return;
    await api('/dietary', { method: 'POST', body: { person_id: personId, requirement, is_allergy: isAllergy ? 1 : 0, notes: notes || null } });
    setRequirement(''); setIsAllergy(false); setNotes('');
    load();
  };

  return (
    <div style={{ marginTop: 16 }}>
      <h4 className="form-section">Dietary requirements</h4>
      {rows.length === 0 && <p className="muted">No dietary requirements recorded.</p>}
      {rows.map(r => (
        <div key={r.id} className="btn-row" style={{ marginBottom: 4, fontSize: 13 }}>
          <span>{r.requirement}</span>
          {!!r.is_allergy && <span className="chip chip-allergy">allergy</span>}
          {r.notes && <span className="muted">{r.notes}</span>}
          <button className="btn btn-ghost btn-sm" onClick={async () => { await api(`/dietary/${r.id}`, { method: 'DELETE' }); load(); }}>remove</button>
        </div>
      ))}
      <div className="btn-row" style={{ marginTop: 8 }}>
        <select className="input" value={requirement} onChange={e => {
          setRequirement(e.target.value);
          setIsAllergy(e.target.value.toLowerCase().includes('allergy'));
        }}>
          <option value="">Add requirement…</option>
          {DIET_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <label style={{ fontSize: 12 }}><input type="checkbox" className="checkbox" checked={isAllergy}
          onChange={e => setIsAllergy(e.target.checked)} /> medically significant allergy</label>
        <input className="input" style={{ width: 180 }} placeholder="Notes" value={notes} onChange={e => setNotes(e.target.value)} />
        <button className="btn btn-sm" onClick={add} disabled={!requirement}>Add</button>
      </div>
    </div>
  );
}
